import { createServerFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';
import { languages } from '@worldcoo/monolith/core';
import { languageToLocale } from '@worldcoo/monolith/core';
import { TranslationServiceClient } from '@worldcoo/monolith/google-cloud/translate';
import { Resource } from 'sst';
import { captcha } from '@worldcoo/monolith/captcha';
import * as z from 'zod';
import { verifySignedCookie, parseIp } from './server-functions-shared.server';

const translateTextsInputSchema = z.object({
	sourceLanguage: z.enum(languages),
	targetLanguages: z.array(z.enum(languages)),
	texts: z.record(z.string(), z.string()),
	captchaToken: z.string(),
});

export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';

export const translateProjectTextsFn = createServerFn({ method: 'POST' })
	.inputValidator(translateTextsInputSchema)
	.handler(async ({ data }) => {
		const sessionNgoId = verifySignedCookie(getCookie('ngo-session'));
		const adminEmail = verifySignedCookie(getCookie('ngo-admin-session'));
		if (!sessionNgoId && !adminEmail) {
			return { error: 'UNAUTHORIZED' as const };
		}

		const requestIp = parseIp();
		if (!requestIp) return { error: 'CAPTCHA_FAILED' as const };
		const captchaResult = await captcha.validate(data.captchaToken, requestIp);
		if (!captchaResult.captchaPassed) {
			return { error: 'CAPTCHA_FAILED' as const };
		}

		const client = new TranslationServiceClient({
			credentials: JSON.parse(Resource.GoogleTranslateCredentialsSecret.value ?? '{}'),
		});

		const sourceLocale = languageToLocale[data.sourceLanguage];
		if (!sourceLocale) return { error: 'TRANSLATION_FAILED' as const };

		const textEntries = Object.entries(data.texts).filter(([, text]) => text.length > 0);
		if (textEntries.length === 0) return {};

		try {
			const results: Record<string, Record<string, string>> = {};

			await Promise.all(
				data.targetLanguages.map(async (targetLang) => {
					const targetLocale = languageToLocale[targetLang];
					if (!targetLocale || targetLocale === sourceLocale) return;

					const [response] = await client.translateText({
						parent: 'projects/worldcoo-467413/locations/global',
						contents: textEntries.map(([, text]) => text),
						mimeType: 'text/plain',
						sourceLanguageCode: sourceLocale,
						targetLanguageCode: targetLocale,
					});

					const translationArray = response.translations?.map((t) => t.translatedText ?? '') ?? [];
					results[targetLang] = Object.fromEntries(textEntries.map(([field], i) => [field, translationArray[i] ?? '']));
				}),
			);

			return results;
		} catch (err: unknown) {
			const e = err as Record<string, unknown> | undefined;
			console.error('Translation failed:', {
				message: err instanceof Error ? err.message : String(err),
				code: e?.code,
				details: e?.details,
				stack: err instanceof Error ? err.stack : undefined,
			});
			return { error: 'TRANSLATION_FAILED' as const };
		}
	});
